import styles from "./styles.module.scss";
import Label from "../Label";
import React, { useState } from "react";
import UserItems from "../UserItems";
import { schedulingFetchDelete } from "app/utils/request/scheduling/fetch";
import { useTranslation } from "react-i18next";
import { RoleType } from "app/contexts/UserContext";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import CancelRegistrationModal from "../CancelRegistrationModal";
import { toast } from "react-toastify";
import RefundConfirmationModal from "../RefundConfirmationModal";
import { useSelector } from "react-redux";
import { RootState } from "app/reducers";

const RegistrantsStudent = ({
  programModel,
  userType,
  onRegistrantDeleted,
  showRegistrantsWaitlisted,
}) => {
  const { t } = useTranslation();
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const registrants = programModel.completedRegistrants;
  const waitlisted = programModel.waitlistedRegistrants;
  const programStatus = programModel.status;
  const hasWaitlist = programModel.withWaitlist;
  const allowAdditionalPeople = programModel.allowAdditionalPeople;

  const currentUserId = useSelector((state: RootState) => state.user.data.id);
  // CN-1323 (CFTB-3343): treat the viewer's own registration AND their managed
  // dependents' (a dependent's headExternalId is the head's external id) as
  // "theirs", so a parent can see and cancel a child's registration even when
  // the program restricts additional people (allowAdditionalPeople === false).
  const belongsToHousehold = (registration: StudentProgramRegistrationModel) =>
    registration.studentProfile.id === currentUserId ||
    registration.studentProfile.headExternalId === currentUserId?.toString();
  const currentRegistrant = registrants.filter(belongsToHousehold);
  const currentWaitlist = waitlisted.filter(belongsToHousehold);


  // Schedulers can see emails, athletes cannot
  const canSeeEmail = userType === RoleType.SCHEDULER;

  const handleRemovedPressed = (registration: StudentProgramRegistrationModel) => {
    setShowRemovePopup(true);
    setSelectedItem(registration);
  };

  const handleRemoved = async () => {
    if (!selectedItem || !selectedItem.registrationId) {
      console.error('Registration ID not found');
      setShowRemovePopup(false);
      return;
    }

    try {
      const response = await schedulingFetchDelete(`/student_program_registrations/${selectedItem.registrationId}`);
      setShowRemovePopup(false);
      onRegistrantDeleted(response);

      // Show refund modal only for students who paid online and have completed registration
      if (selectedItem.paymentMethod === 'online' && selectedItem.status === 'completed') {
        setShowRefundModal(true);
      }
    } catch (error) {
      console.error('Failed to delete registration:', error);
      if (error?.status === 422) {
        toast.error(error.error || t("Payment is currently being processed. Please try again shortly."));
      }
      setShowRemovePopup(false);
    }
  };

  return (
    <div className={`${styles.container} ${styles.top_margin_16}`}>
      {((registrants?.length > 0) || (waitlisted?.length > 0) || showRegistrantsWaitlisted) && (
        <div className={styles.line_container}>
          <div className={styles.square_container}>
            <Label
              text={
                programStatus === "completed"
                  ? `${t("Registered")} • ${registrants?.length}`
                  : `${t("Already Registered")} • ${registrants?.length || 0}`
              }
              textMarginLeft={15}
              item={(
                <div>
                  <div className={styles.line_separator} />
                  <UserItems
                    showEmail={canSeeEmail}
                    showPaymentMethod={true}
                    userList={allowAdditionalPeople ? registrants : currentRegistrant}
                    onClose={(item) => handleRemovedPressed(item)}
                    allowRemove={true}
                  />
                </div>
              )}
            />
          </div>
          {(hasWaitlist || showRegistrantsWaitlisted) && (
            <div className={styles.square_container}>
              <Label
                text={`${t("Waitlisted")} • ${waitlisted?.length || 0}`}
                textMarginLeft={15}
                item={(
                    <div>
                      <div className={styles.line_separator} />
                      <UserItems
                        showEmail={canSeeEmail}
                        showPaymentMethod={true}
                        userList={allowAdditionalPeople ? waitlisted : currentWaitlist}
                        onClose={(item) => handleRemovedPressed(item)}
                        allowRemove={true}
                        waitlisted={true}
                      />
                    </div>
                )}
              />
            </div>
          )}
        </div>
      )}

      <CancelRegistrationModal
        isOpen={showRemovePopup}
        studentName={`${selectedItem?.studentProfile?.firstName} ${selectedItem?.studentProfile?.lastName}`}
        onConfirm={handleRemoved}
        onCancel={() => setShowRemovePopup(false)}
      />

      <RefundConfirmationModal
        isOpen={showRefundModal}
        programName={programModel.title}
        onClose={() => setShowRefundModal(false)}
      />

    </div>
  );
};

export default RegistrantsStudent;
